import { supabase } from '../lib/supabase';
import { subscriptionService } from '../services/subscription';

export const DAILY_FREE_LIMIT = 1;
export const ANON_CASE_USAGE_KEY = 'simPLAB2_anonCaseUsed';

// Get today's date in ISO format (YYYY-MM-DD)
function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

// Check if a user has a premium subscription
async function isPremiumActive(userEmail?: string | null): Promise<boolean> {
  if (!userEmail) return false;
  try {
    // Always check with the server for most up-to-date subscription status
    const { status } = await subscriptionService.checkSubscriptionStatus(userEmail);
    return status === 'premium'; // Only 'premium' counts, not 'trial' or 'expired'
  } catch (error) {
    console.error('Error checking premium status:', error);
    return false;
  }
}

// Track case usage in Supabase
export async function trackCaseUsage(userEmail?: string | null): Promise<boolean> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    // Handle anonymous (non-registered) users with localStorage
    if (!user) {
      // Check if anonymous user has already used their free case
      const hasUsedFreeCase = localStorage.getItem(ANON_CASE_USAGE_KEY) === 'true';
      
      // If they haven't used it yet, mark it as used now
      if (!hasUsedFreeCase) {
        localStorage.setItem(ANON_CASE_USAGE_KEY, 'true');
        return false; // Not exceeded yet (first use)
      }
      
      // Return true if they've already used their free case
      return true;
    }

    // Check if premium
    const isPremium = await isPremiumActive(userEmail || user.email);
    if (isPremium) return false; // Premium users have unlimited access

    const today = getTodayDate();

    // Get current usage for today
    const { data: existingUsage, error: fetchError } = await supabase
      .from('case_usage')
      .select('*')
      .eq('user_id', user.id)
      .eq('date', today)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = not found
      console.error('Error fetching case usage:', fetchError);
      return true; // Error = limit exceeded (fail safe)
    }

    if (existingUsage) {
      // Update existing record
      const newCount = existingUsage.count + 1;
      await supabase
        .from('case_usage')
        .update({ count: newCount, updated_at: new Date().toISOString() })
        .eq('id', existingUsage.id);
      
      return newCount > DAILY_FREE_LIMIT;
    } else {
      // Create new record for today
      await supabase
        .from('case_usage')
        .insert({
          user_id: user.id,
          date: today,
          count: 1
        });
      
      return 1 > DAILY_FREE_LIMIT; // Usually false unless DAILY_FREE_LIMIT is 0
    }
  } catch (error) {
    console.error('Error tracking case usage:', error);
    return true; // Error = limit exceeded (fail safe)
  }
}

// Get remaining cases for today
export async function getRemainingCases(userEmail?: string | null): Promise<number> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    // Handle anonymous (non-registered) users with localStorage
    if (!user) {
      // Check if anonymous user has already used their free case
      const hasUsedFreeCase = localStorage.getItem(ANON_CASE_USAGE_KEY) === 'true';
      
      // Return 0 if they've used their case, otherwise 1
      return hasUsedFreeCase ? 0 : 1;
    }

    // Check if premium
    const isPremium = await isPremiumActive(userEmail || user.email);
    if (isPremium) return Infinity; // Premium users have unlimited cases

    const today = getTodayDate();

    // Get current usage for today
    const { data: existingUsage, error: fetchError } = await supabase
      .from('case_usage')
      .select('count')
      .eq('user_id', user.id)
      .eq('date', today)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error fetching case usage:', fetchError);
      return 0; // Error = no remaining cases (fail safe)
    }

    const currentCount = existingUsage?.count || 0;
    return Math.max(0, DAILY_FREE_LIMIT - currentCount);
  } catch (error) {
    console.error('Error getting remaining cases:', error);
    return 0; // Error = no remaining cases (fail safe)
  }
}
